import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from './components/Header';
import InfoCard from './components/InfoCard';
import ScanUploadCard from './components/ScanUploadCard';
import DashboardWorkflowOverview from './components/DashboardWorkflowOverview';
import { useWorkflowState, useWorkflowActions } from '../../shared/stores/useWorkflowStore';
import {
  WORKFLOW_TYPES,
  SCAN_PROCESS_STEPS,
  GLOBAL_STEP_ORDER,
  PATHS,
  canAccessStepGlobally,
  getWorkflowTypeForStep,
} from '../../shared/constants/flowConfig';
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../../shared/stores/useEditorStore';
import {
  validateSTLFiles,
  validatePLYFiles,
  validateOBJFiles,
  createBlobUrl,
  createOBJBlobUrls,
} from '../../shared/lib/fileUploadUtils';
import { saveBkDesignOrderContext } from '../../shared/lib/bkDesignOrderContext';
import { hasMeshSourceOnServer } from '../../shared/lib/bkScanSourceFiles';
import { deriveResumeFromMeta } from '../../shared/lib/bkResumeState';
import { clearAllStepCheckpoints } from '../step-checkpoints';

const OrderIntakeStep = () => {
  const [patientData, isLoadingPatientData, currentWorkflow, stepCompletion, scanFilesMeta, isFetchingScanFiles, scanAutoLoaded, isUploadingScanFiles] = useWorkflowState(
    useShallow((state) => [
      state.patientData,
      state.isLoadingPatientData,
      state.currentWorkflow,
      state.stepCompletion,
      state.scanFilesMeta,
      state.isFetchingScanFiles,
      state.scanAutoLoaded,
      state.isUploadingScanFiles,
    ])
  );
  const { fetchPatientData, setCurrentWorkflow, setCurrentStep, setStepCompletion, setScanAutoLoaded, uploadAndProceed, setBkMainProcessIntent } = useWorkflowActions();
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('stl');
  const [uploadError, setUploadError] = useState(null);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);
  const fileInputRef = useRef(null);
  const setUploadedModelData = useEditorStore((state) => state.setUploadedModelData);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const salesOrder = searchParams.get('sales_order');
  const customer = searchParams.get('customer');

  const serverSourceFiles = scanFilesMeta?.source_files;
  const hasServerSourceFiles = Array.isArray(serverSourceFiles) && serverSourceFiles.length > 0;
  const hasMeshOnServer = hasMeshSourceOnServer(serverSourceFiles);
  /** Server already has source files — do not offer another upload from the dashboard */
  const hideLocalUpload = hasServerSourceFiles;

  // Persist order context for /design/* after refresh (URL query is not on design routes)
  useEffect(() => {
    if (salesOrder && customer) {
      saveBkDesignOrderContext(salesOrder, customer);
    }
  }, [salesOrder, customer]);

  // Fetch patient data + scan files on mount (once per page load; stop on error)
  useEffect(() => {
    if (salesOrder && customer && !patientData && !isLoadingPatientData && !isFetching && !fetchError) {
      setIsFetching(true);
      fetchPatientData(salesOrder, customer)
        .catch((err) => {
          console.error('Failed to fetch patient data:', err);
          setFetchError(err?.message || 'Failed to load patient details');
        })
        .finally(() => setIsFetching(false));
    }
  }, [salesOrder, customer, patientData, isLoadingPatientData, isFetching, fetchError, fetchPatientData]);

  useEffect(() => {
    if (patientData && !isLoadingPatientData) {
      setStepCompletion('FETCH_DETAILS');
      setCurrentWorkflow(WORKFLOW_TYPES.SCAN_PROCESS);
      setCurrentStep(SCAN_PROCESS_STEPS.PREPARING_SCAN);
    }
  }, [patientData, isLoadingPatientData, setStepCompletion, setCurrentWorkflow, setCurrentStep]);

  const fetchPatientButtonOnClick = async () => {
    if (!salesOrder || !customer) {
      console.error('Missing sales_order or customer in URL params');
      return;
    }
    try {
      setFetchError(null);
      setIsFetching(true);
      await fetchPatientData(salesOrder, customer);
    } catch (error) {
      console.error("Error fetching patient data:", error);
      setFetchError(error?.message || 'Failed to load patient details');
    } finally {
      setIsFetching(false);
    }
  };

  const handleFormatChange = (e) => {
    setSelectedFormat(e.target.value);
    setUploadError(null);
    setSelectedFileNames([]);
    setLocalFiles([]);
    clearAllStepCheckpoints();
    setUploadedModelData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setUploadError(null);
    if (files.length === 0) return;

    try {
      let validationResult;

      if (selectedFormat === 'stl') {
        validationResult = validateSTLFiles(files);
        if (!validationResult.isValid) {
          setUploadError(validationResult.error);
          return;
        }
        const blobUrl = createBlobUrl(validationResult.stlFile);
        setUploadedModelData({ type: 'stl', data: blobUrl, source: 'local_upload' });
        setSelectedFileNames([validationResult.stlFile.name]);
        setLocalFiles([validationResult.stlFile]);
      } else if (selectedFormat === 'ply') {
        validationResult = validatePLYFiles(files);
        if (!validationResult.isValid) {
          setUploadError(validationResult.error);
          return;
        }
        const blobUrl = createBlobUrl(validationResult.plyFile);
        setUploadedModelData({ type: 'ply', data: blobUrl, source: 'local_upload' });
        setSelectedFileNames([validationResult.plyFile.name]);
        setLocalFiles([validationResult.plyFile]);
      } else {
        validationResult = validateOBJFiles(files);
        if (!validationResult.isValid) {
          setUploadError(validationResult.error);
          return;
        }
        const blobUrls = createOBJBlobUrls(validationResult);
        setUploadedModelData({ type: 'obj', data: blobUrls, source: 'local_upload' });
        const allFiles = [
          validationResult.objFile,
          ...(validationResult.mtlFile ? [validationResult.mtlFile] : []),
          ...validationResult.textureFiles,
        ];
        setSelectedFileNames(allFiles.map((f) => f.name));
        setLocalFiles(allFiles);
      }
    } catch (error) {
      setUploadError(`Error processing files: ${error.message}`);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const applySelectedFiles = (files) => {
    if (!files || files.length === 0) {
      setSelectedFileNames([]);
      setLocalFiles([]);
      clearAllStepCheckpoints();
      setUploadedModelData(null);
      return true;
    }

    clearAllStepCheckpoints();

    let validationResult;

    if (selectedFormat === 'stl') {
      validationResult = validateSTLFiles(files);
      if (!validationResult.isValid) {
        setUploadError(validationResult.error);
        return false;
      }
      const blobUrl = createBlobUrl(validationResult.stlFile);
      setUploadedModelData({ type: 'stl', data: blobUrl, source: 'local_upload' });
      setSelectedFileNames([validationResult.stlFile.name]);
      setLocalFiles([validationResult.stlFile]);
      return true;
    }

    if (selectedFormat === 'ply') {
      validationResult = validatePLYFiles(files);
      if (!validationResult.isValid) {
        setUploadError(validationResult.error);
        return false;
      }
      const blobUrl = createBlobUrl(validationResult.plyFile);
      setUploadedModelData({ type: 'ply', data: blobUrl, source: 'local_upload' });
      setSelectedFileNames([validationResult.plyFile.name]);
      setLocalFiles([validationResult.plyFile]);
      return true;
    }

    validationResult = validateOBJFiles(files);
    if (!validationResult.isValid) {
      setUploadError(validationResult.error);
      return false;
    }

    const blobUrls = createOBJBlobUrls(validationResult);
    setUploadedModelData({ type: 'obj', data: blobUrls, source: 'local_upload' });
    const allFiles = [
      validationResult.objFile,
      ...(validationResult.mtlFile ? [validationResult.mtlFile] : []),
      ...validationResult.textureFiles,
    ];
    setSelectedFileNames(allFiles.map((f) => f.name));
    setLocalFiles(allFiles);
    return true;
  };

  const clearSelectedLocalFiles = () => {
    setSelectedFileNames([]);
    setLocalFiles([]);
    clearAllStepCheckpoints();
    setUploadedModelData(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveSelectedFile = (indexToRemove) => {
    setUploadError(null);
    const nextLocalFiles = localFiles.filter((_, idx) => idx !== indexToRemove);
    const applied = applySelectedFiles(nextLocalFiles);
    if (!applied) {
      // Keep the user-selected file list after removing the file,
      // but clear preview data if the remaining set is invalid.
      setLocalFiles(nextLocalFiles);
      setSelectedFileNames(nextLocalFiles.map((f) => f.name));
      setUploadedModelData(null);
    }
  };

  const getAcceptAttribute = () => {
    if (selectedFormat === 'stl') return '.stl';
    if (selectedFormat === 'ply') return '.ply';
    if (selectedFormat === 'obj') return '.obj,.mtl,.jpg,.jpeg,.png';
    return '*';
  };

  const withQuery = (path) => {
    const q = salesOrder && customer ? `?sales_order=${encodeURIComponent(salesOrder)}&customer=${encodeURIComponent(customer)}` : '';
    return `${path}${q}`;
  };

  const proceedToDesign = () => {
    if (!patientData) return;

    // Resume from the latest saved checkpoint. stepCompletion is in-memory only
    // and resets on reload, so derive it (and the resume step + intent) from the
    // server meta — otherwise we always land on the earliest step.
    const { stepCompletion: derived, resumeStep, intent } = deriveResumeFromMeta(scanFilesMeta);

    const mergedCompletion = { ...stepCompletion, ...derived };
    Object.entries(derived).forEach(([s, done]) => {
      if (done) setStepCompletion(s, true);
    });

    const step =
      resumeStep && canAccessStepGlobally(resumeStep, mergedCompletion)
        ? resumeStep
        : GLOBAL_STEP_ORDER[0];

    setBkMainProcessIntent(intent);

    const workflow = getWorkflowTypeForStep(step);
    if (workflow) setCurrentWorkflow(workflow);
    setCurrentStep(step);
    navigate(withQuery(PATHS[step]));
  };

  const handleStartUploading = async () => {
    if (scanAutoLoaded) {
      proceedToDesign();
      return;
    }

    if (hideLocalUpload && localFiles.length > 0) {
      setUploadError('This order already has source files on the server. Upload is not available here.');
      return;
    }

    if (localFiles.length === 0) {
      if (hasMeshOnServer) {
        proceedToDesign();
        return;
      }
      if (hideLocalUpload) {
        setUploadError('No 3D mesh on the server for this order. Add or replace scan files from BengaitLabs, then refresh.');
        return;
      }
      setUploadError('Please select a scan file first.');
      return;
    }

    const success = await uploadAndProceed(salesOrder, customer, localFiles);
    if (success) {
      proceedToDesign();
    } else {
      setUploadError('Failed to upload scan files. Please try again.');
    }
  };

  const getStepperButton = () => {
    if (!patientData) {
      return (
        <button
          type="button"
          onClick={fetchPatientButtonOnClick}
          disabled={isFetching}
          className="cursor-pointer rounded-md border border-transparent bg-[var(--odp-purple)] px-5 py-2.5 text-sm font-medium text-white touch-manipulation transition-colors hover:bg-[var(--odp-purple-dark)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--focus-ring,#0070f3)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-400 disabled:opacity-85"
        >
          {isFetching ? 'Fetching…' : 'Fetch Patient Details'}
        </button>
      );
    }

    const isUploading = isUploadingScanFiles;
    const needsMeshFromLabs =
      hideLocalUpload && !hasMeshOnServer && !scanAutoLoaded && localFiles.length === 0;

    let label = 'Start Uploading';
    if (scanAutoLoaded || (hasMeshOnServer && localFiles.length === 0)) {
      label = 'Proceed to Design';
    }
    if (isUploading) label = 'Uploading…';
    if (needsMeshFromLabs) label = 'Add 3D mesh from BengaitLabs';

    return (
      <button
        type="button"
        onClick={handleStartUploading}
        disabled={isUploading || needsMeshFromLabs}
        title={
          needsMeshFromLabs
            ? 'Source files exist but no STL/PLY/OBJ. Add a mesh on the order in BengaitLabs, then refresh.'
            : undefined
        }
        className="cursor-pointer rounded-md border border-transparent bg-[var(--odp-purple)] px-5 py-2.5 text-sm font-medium text-white touch-manipulation transition-colors hover:bg-[var(--odp-purple-dark)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--focus-ring,#0070f3)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-400 disabled:opacity-85"
      >
        {label}
      </button>
    );
  };

  const showScanUploadSection = patientData && currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS && !scanAutoLoaded;

  const showLoading = (isLoadingPatientData || isFetching) && salesOrder && customer;

  return (
    <div className="flex flex-col bg-[#fafafa] text-[var(--text-ink,#171717)] min-h-dvh">
      <Header />

      <main className="box-border mx-auto flex w-full max-w-7xl flex-1 flex-col gap-8 overflow-y-auto px-4 py-5 pb-8 sm:px-6 sm:py-6 sm:pb-8 lg:px-8 lg:py-7 lg:pb-10">
        {salesOrder && customer && (
          <div
            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            role="status"
          >
            <div className="min-w-0 flex-1">
              <p className="m-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
                Current order
              </p>
              <p className="mt-1.5 mb-0 text-sm leading-snug text-[var(--text-primary)]">
                <span className="font-semibold">{salesOrder}</span>
                <span className="mx-2 select-none text-neutral-400" aria-hidden>
                  ·
                </span>
                <span>{customer}</span>
              </p>
            </div>
            <div className="shrink-0 self-stretch sm:self-center">{getStepperButton()}</div>
          </div>
        )}

        {showLoading && !patientData && !fetchError && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[repeat(auto-fit,minmax(280px,1fr))]">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border border-black/[0.08] bg-white p-[1.15rem]" aria-hidden>
                <div className="motion-safe:animate-pulse motion-reduce:animate-none">
                  <div className="mb-[0.85rem] h-[1.15rem] w-[40%] rounded bg-gray-200" />
                  <div className="mb-[0.65rem] h-[0.85rem] w-full rounded bg-gray-100" />
                  <div className="mb-[0.65rem] h-[0.85rem] w-full rounded bg-gray-100 w-[80%]" />
                  <div className="mb-[0.65rem] h-[0.85rem] w-full rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        )}

        {fetchError && !patientData && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-center shadow-sm">
            <p className="mb-2 mt-0 text-sm font-semibold text-red-800">
              Failed to load patient details
            </p>
            <p className="mb-4 mt-0 text-xs leading-snug text-red-700">{fetchError}</p>
            <button
              type="button"
              onClick={fetchPatientButtonOnClick}
              disabled={isFetching}
              className="cursor-pointer rounded-md border border-transparent bg-[var(--odp-purple)] px-4 py-2 text-[0.8125rem] font-medium text-white touch-manipulation transition-colors hover:bg-[var(--odp-purple-dark)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--focus-ring,#0070f3)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        )}

        {patientData && (
          <>
            <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))]">
              <InfoCard title="Patient Details" data={patientData.patientDetails} />
              <InfoCard title="Assessment" data={patientData.assessmentDetails} />
              <InfoCard title="Design Modifications" data={patientData.globalReduction} />
              {showScanUploadSection && (
                <ScanUploadCard
                  selectedFormat={selectedFormat}
                  onFormatChange={handleFormatChange}
                  selectedFileNames={selectedFileNames}
                  uploadError={uploadError}
                  hideLocalUpload={hideLocalUpload}
                  hasServerSourceFiles={hasServerSourceFiles}
                  serverSourceFiles={serverSourceFiles}
                  isFetchingScanFiles={isFetchingScanFiles}
                  fileInputRef={fileInputRef}
                  onBrowseClick={() => fileInputRef.current?.click()}
                  onFileSelect={handleFileSelect}
                  onRemoveFile={handleRemoveSelectedFile}
                  onClearAll={clearSelectedLocalFiles}
                  getAcceptAttribute={getAcceptAttribute}
                />
              )}
            </div>
            <DashboardWorkflowOverview />
          </>
        )}

      </main>

    </div>

  );
};

export default OrderIntakeStep;
